import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkle, FileArrowUp, ChatCircleDots, Lightning, ArrowUpRight, CheckCircle, X,
} from "@phosphor-icons/react";
import client from "../lib/api";
import { Button, Input, Modal } from "./ui";
import { TL } from "../i18n";
import { toast } from "sonner";

const money = (n) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));

function Meter({ label, used, limit }) {
  const pct = limit ? Math.min((used / limit) * 100, 100) : 0;
  return (
    <div className="flex-1" data-testid={`ai-meter-${label.toLowerCase()}`}>
      <div className="mb-1 flex items-center justify-between text-[11px]">
        <span className="opacity-70">{label}</span>
        <span className="tabular-nums font-medium">{used}/{limit}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct >= 100 ? "#DA1E28" : "#8A3FFC" }} />
      </div>
    </div>
  );
}

export default function AiStatementReader() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState("");
  const fileRef = useRef();
  const [upOpen, setUpOpen] = useState(false);
  const [upBusy, setUpBusy] = useState("");

  const refresh = () => client.get("/ai/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  // Handle return from Stripe tier checkout
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const sid = p.get("tier_session_id");
    if (!sid) return;
    let tries = 0;
    const poll = () => {
      client.get(`/payments/status/${sid}`).then((r) => {
        const ps = r.data?.payment_status;
        if (ps === "paid") {
          toast.success(TL("Plan upgraded — your AI allowance is unlocked!"));
          refresh();
          window.history.replaceState({}, "", window.location.pathname);
        } else if (ps === "expired" || tries > 12) {
          window.history.replaceState({}, "", window.location.pathname);
        } else { tries += 1; setTimeout(poll, 1500); }
      }).catch(() => {});
    };
    poll();
  }, []);

  const upgrade = async (tierKey) => {
    setUpBusy(tierKey);
    try {
      const r = await client.post("/payments/tier-checkout", { tier: tierKey, origin_url: window.location.origin });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || TL("Could not start checkout"));
      setUpBusy("");
    }
  };

  const manageBilling = async () => {
    try {
      const r = await client.post("/payments/billing-portal", { origin_url: window.location.origin });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || TL("Billing portal unavailable"));
    }
  };

  const parses = status?.parses || { used: 0, limit: 0, remaining: 0 };
  const questions = status?.questions || { used: 0, limit: 0, remaining: 0 };
  const canParse = parses.limit > 0 && parses.remaining > 0;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setParsed(null);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await client.post("/ai/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(r.data);
      setStatus(r.data.status);
      toast.success(TL("Statement read successfully"));
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not read that document";
      toast.error(msg);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const applyHolding = async () => {
    const s = parsed?.suggested_holding;
    if (!s) return;
    setBusy(true);
    try {
      const { event, ...fields } = s;
      const created = await client.post("/holdings", { data: fields });
      const id = created.data?.id || created.data?.data?.id;
      if (event && id && event.date) {
        const kind = event.type === "distribution" ? "distribution" : "call";
        await client.post(`/holdings/${id}/commitment-events/${kind}`, { date: event.date, amount: event.amount || 0, note: "AI Statement Reader" });
      }
      toast.success(`Added “${fields.name}” to your holdings`);
      setParsed(null);
      setTimeout(() => window.location.reload(), 700);
    } catch (err) {
      toast.error(err?.response?.data?.detail || TL("Could not add holding"));
    } finally {
      setBusy(false);
    }
  };

  const ask = async () => {
    if (!q.trim() || questions.remaining <= 0) return;
    setBusy(true); setAnswer("");
    try {
      const r = await client.post("/ai/ask", { question: q.trim() });
      setAnswer(r.data.answer);
      setStatus(r.data.status);
    } catch (err) {
      toast.error(err?.response?.data?.detail || TL("AI request failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 rounded-2xl border border-[#8A3FFC]/30 bg-gradient-to-br from-[#8A3FFC]/8 to-transparent p-4" data-testid="ai-statement-reader">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-base font-semibold">
          <Sparkle size={20} weight="fill" className="text-[#8A3FFC]" />
          AI Statement Reader
          <span className="rounded-full bg-[#8A3FFC]/10 px-2 py-0.5 text-[10px] font-medium text-[#8A3FFC]" data-testid="ai-tier-badge">
            {status?.tier_name || "…"} plan
          </span>
        </div>
        <div className="flex w-full max-w-md items-center gap-4">
          <Meter label={TL("Parses")} used={parses.used} limit={parses.limit} />
          <Meter label={TL("Questions")} used={questions.used} limit={questions.limit} />
          <Button variant="ghost" className="shrink-0 border border-[#8A3FFC]/40 text-xs text-[#8A3FFC]" onClick={() => setUpOpen(true)} data-testid="ai-upgrade-open">
            <Lightning size={14} weight="fill" className="me-1" />Upgrade
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* READER */}
        <div className="rounded-xl border border-black/10 bg-white/60 p-3 backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <FileArrowUp size={16} weight="duotone" className="text-[#8A3FFC]" />Read a statement or capital call
          </div>
          {canParse ? (
            <>
              <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={onFile} disabled={busy} className="hidden" data-testid="ai-file-input" />
              <Button variant="ghost" className="w-full border border-dashed border-[#8A3FFC]/40 py-6 text-sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="ai-upload-btn">
                {busy ? "Reading…" : "Drop a PDF / image — I'll extract the details"}
              </Button>
              <p className="mt-1 text-[11px] opacity-50">Nothing is created until you review and confirm. {parses.remaining} parse(s) left this month.</p>
            </>
          ) : (
            <div className="rounded-lg border border-[#F1C21B]/40 bg-[#F1C21B]/10 p-3 text-sm" data-testid="ai-upgrade-prompt">
              <div className="flex items-center gap-2 font-medium"><Lightning size={16} weight="fill" className="text-[#F1C21B]" />{TL("Statement Reader is a paid add-on")}</div>
              <p className="mt-1 text-xs opacity-80">
                {parses.limit === 0
                  ? `Your ${status?.tier_name || "Diamond"} plan doesn’t include statement parsing.`
                  : `You’ve used all ${parses.limit} parses this month.`}
                {" "}{TL("Upgrade to Platinum, Rhodium or Osmium to auto-read statements.")}
              </p>
              <button onClick={() => setUpOpen(true)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#8A3FFC]" data-testid="ai-upgrade-link">
                Upgrade now <ArrowUpRight size={12} />
              </button>
            </div>
          )}

          <AnimatePresence>
            {parsed && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                className="mt-3 overflow-hidden rounded-lg border border-black/10 bg-black/[0.02] p-3 dark:border-white/10 dark:bg-white/[0.03]" data-testid="ai-parsed-result">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#8A3FFC]">{parsed.fields?.document_type?.replace("_", " ") || "document"}</span>
                  <button onClick={() => setParsed(null)} className="opacity-50 hover:opacity-100"><X size={14} /></button>
                </div>
                <p className="mb-2 text-xs opacity-70">{parsed.fields?.summary}</p>
                <div className="grid grid-cols-2 gap-1.5 text-xs">
                  {[["Name", parsed.fields?.holding_name], ["Issuer", parsed.fields?.issuer], ["Category", parsed.fields?.category],
                    ["Currency", parsed.fields?.currency], ["Commitment", money(parsed.fields?.commitment)],
                    ["Called", money(parsed.fields?.called_amount)], ["Distributed", money(parsed.fields?.distributed_amount)],
                    ["NAV", money(parsed.fields?.nav)], ["Date", parsed.fields?.date], ["Due", parsed.fields?.due_date]]
                    .filter(([, v]) => v && v !== "—").map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-2 border-b border-black/5 py-0.5 dark:border-white/5" data-testid={`ai-field-${k.toLowerCase()}`}>
                        <span className="opacity-60">{k}</span><span className="font-medium">{v}</span>
                      </div>
                    ))}
                </div>
                <Button className="mt-3 w-full" onClick={applyHolding} disabled={busy} data-testid="ai-apply-btn">
                  <CheckCircle size={16} className="me-1" />Add to my holdings
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ASK */}
        <div className="rounded-xl border border-black/10 bg-white/60 p-3 backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <ChatCircleDots size={16} weight="duotone" className="text-[#8A3FFC]" />Ask your data
          </div>
          <div className="flex gap-2">
            <Input placeholder={TL("e.g. What is my largest holding?")} value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()} disabled={busy || questions.remaining <= 0} className="flex-1" data-testid="ai-ask-input" />
            <Button onClick={ask} disabled={busy || !q.trim() || questions.remaining <= 0} data-testid="ai-ask-btn">{TL("Ask")}</Button>
          </div>
          {questions.remaining <= 0 ? (
            <p className="mt-1 text-[11px] text-[#DA1E28]" data-testid="ai-ask-limit">Question limit reached for the {status?.tier_name} plan — upgrade for more.</p>
          ) : (
            <p className="mt-1 text-[11px] opacity-50">{questions.remaining} question(s) left this month. Answers are grounded only in your holdings.</p>
          )}
          <AnimatePresence>
            {answer && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="mt-3 whitespace-pre-wrap rounded-lg border border-black/10 bg-black/[0.02] p-3 text-sm dark:border-white/10 dark:bg-white/[0.03]" data-testid="ai-answer">
                {answer}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <Modal open={upOpen} onClose={() => setUpOpen(false)} title={TL("Upgrade your support plan")} testid="ai-upgrade-modal">
        <div className="space-y-3">
          <p className="text-xs opacity-70">
            Flat monthly pricing — no AUM fees. Plans <b>{TL("auto-renew monthly")}</b> and upgrading unlocks your new
            AI allowance instantly (mid-cycle upgrades are prorated). You’re currently on <b>{status?.tier_name || "Diamond"}</b>.
          </p>
          <div className="grid gap-2">
            {(status?.tiers || []).filter((tr) => tr.key !== "diamond").map((tr) => {
              const current = tr.key === status?.tier;
              const TONE = { platinum: "#8A3FFC", rhodium: "#08BDBA", osmium: "#F1C21B" }[tr.key];
              return (
                <div key={tr.key} className="flex items-center gap-3 rounded-lg border border-black/10 p-3 dark:border-white/10" data-testid={`ai-upgrade-tier-${tr.key}`}>
                  <div className="flex-1">
                    <div className="text-sm font-semibold" style={{ color: TONE }}>{tr.name} — ${tr.monthly_usd.toFixed(2)}/mo</div>
                    <div className="text-xs opacity-60">{tr.parses} parses · {tr.questions} questions · {tr.sla_hours}h targeted response</div>
                  </div>
                  <Button onClick={() => upgrade(tr.key)} disabled={current || !!upBusy} data-testid={`ai-upgrade-choose-${tr.key}`}>
                    {current ? "Current" : upBusy === tr.key ? "…" : "Choose"}
                  </Button>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] opacity-50">{TL("Applicable tax is added at checkout. Billed monthly, auto-renews. Cancel anytime in the billing portal.")}</p>
            {status?.tier && status.tier !== "diamond" ? (
              <Button variant="ghost" className="shrink-0 border border-black/15 text-xs dark:border-white/15" onClick={manageBilling} data-testid="ai-manage-billing">{TL("Manage billing")}</Button>
            ) : null}
          </div>
        </div>
      </Modal>
    </div>
  );
}
